import BaseCan from "../../../common/abilities/baseCan";
import Folder from "../models/folder";
import Form from "../models/form";
import License from "../models/license";
import { appGroupDocCapabilities, DocumentRoleCapability } from "../../../common/enums/roles/capabilities/documentRoleCapability";
import { OrganizationRoleCapability } from "../../../common/enums/roles/capabilities/organizationRoleCapability";
import UserDecorator from "../../admin/models/userDecorator";
import User from "../../../common/models/user";
import Certificate from "../models/certificate";
import Organization from "../../../common/models/organization";
import Application from "../models/application";
import PublicProfile from '~/models/publicProfile/publicProfile';
import GroupDefinition from '~/definitions/groupDefinition'
import { GroupRoleCapability } from "@/common/enums/roles/capabilities/groupRoleCapability";
import { IMemberUser } from '~/interfaces/organizationInterfaces'
import ApplicationDefinition from '~/definitions/applicationDefinition';

export default class DocumentManagementCan extends BaseCan {
  constructor(user: User | UserDecorator) {
    super(user);
    const documentModels = [Form, License, Certificate];
    const shareModels = [Form, License, Folder, Certificate, PublicProfile];
    type shareModelsType = Form | License | Folder | Certificate;

    // Supported syntaxes:
    // this.iCan('share', Folder, folder => true);
    // this.iCan(['share'], Folder, folder => true);
    // this.iCan('share', [Folder], folder => true);
    // this.iCan(['share'], [Folder], folder => true);
    this.iCan('createDocumentsAndFolders', Folder, folder => {
      return folder.organization.hasCapability(OrganizationRoleCapability.createDocumentsAndFolders) ||
        folder.hasShareCapability(user, DocumentRoleCapability.createDocumentsFolders);
    });

    this.iCan('createModeling', Folder, folder => {
      return user.hasOrganizationCapability(OrganizationRoleCapability.manageObjectModels) ||
        folder.hasShareCapability(user, DocumentRoleCapability.createModeling);
    });

    this.iCan('share', shareModels, document => {
      return document.organization.hasCapability(OrganizationRoleCapability.manageAllDocumentAndFolderSharesAndPermissions) ||
        document.hasShareCapability(user, DocumentRoleCapability.manageSharesAndPermissions);
    });

    this.iCan('viewShares', shareModels, document => {
      return true; // Object Models don't have shares so checking this ability for shareModels enforces that
    });

    // @param data.share: document share from modal
    // @param user.share: current user's share for document. @see shareDocument.vue organizationStore/setCurrentUserDocumentShare
    this.iCan('transferOwner', shareModels, (document: shareModelsType, data: { share: { role_ids: Array<string>, reference_id: String }, organization: Organization }) => {
      let documentCan = this.can('share', document);
      if (user.share?.role_ids?.includes(data.organization.document_owner_role_id)) {
        documentCan = documentCan && data.share && data.share.reference_id !== user.id;
      } else {
        documentCan = documentCan && document.hasShareCapability(user, DocumentRoleCapability.manageSharesAndPermissions);
      }

      const organizationCan = data.organization.hasCapability([
        OrganizationRoleCapability.manageMembers,
        OrganizationRoleCapability.manageAllDocumentAndFolderSharesAndPermissions,
      ]);

      return documentCan || organizationCan;
    });

    this.iCan('edit', shareModels, document => {
      return document.organization.hasCapability(OrganizationRoleCapability.editAllDocumentsAndFolders) ||
        document.hasShareCapability(user, DocumentRoleCapability.edit);
    });

    this.iCan('clone', Form, document => {
      return this.can('edit', document) && document.organization.hasCapability(OrganizationRoleCapability.createDocumentsAndFolders);
    });

    this.iCan('moveArchive', shareModels, document => {
      return document.organization.hasCapability(OrganizationRoleCapability.moveArchiveAllDocumentsAndFolders) ||
        document.hasShareCapability(user, DocumentRoleCapability.moveArchive);
    });

    this.iCan('moveArchiveChild', Folder, folder => {
      return folder.organization.hasCapability(OrganizationRoleCapability.moveArchiveAllDocumentsAndFolders) ||
        folder.isAnyInDescendants(childFolder => childFolder.hasShareCapability(user, DocumentRoleCapability.moveArchive));
    });

    this.iCan('viewContextMenu', Folder, document => {
      return this.can('edit', document) ||
        this.can('createDocumentsAndFolders', document) ||
        this.can('moveArchive', document);
    });

    this.iCan('viewContextMenu', documentModels, document => {
      return (!document.isArchive && (
        this.can('edit', document) ||
        this.can('share', document) ||
        this.can('preview', document) ||
        this.can('moveArchive', document) ||
        this.can('publishUnpublish', document)
      ));
    });

    this.iCan('publishUnpublish', documentModels, document => {
      return document.organization.hasCapability(OrganizationRoleCapability.publishUnpublishAllOrganizationalDocuments) ||
        document.hasShareCapability(user, DocumentRoleCapability.publishUnpublish);
    });

    this.iCan('preview', documentModels, document => {
      return this.can('publishUnpublish', document) ||
        this.can('edit', document);
    });

    this.iCan('viewDocumentsOnSubmissionsDashboard', documentModels, document => {
      return document.organization.hasCapability([OrganizationRoleCapability.viewAllDocumentsOnSubmissionsDashboard, OrganizationRoleCapability.viewAllSubmissionDetailsAndExport]) ||
        document.hasShareCapability(user, [DocumentRoleCapability.viewOnSubmissionsDashboard, DocumentRoleCapability.viewSubmissionDetailsAndExport]);
    });

    this.iCan('selectFolder', Organization, organization => {
      return organization.hasCapability(OrganizationRoleCapability.editAllDocumentsAndFolders) ||
        (organization.folders[0].folders.length > 0)
    });

    this.iCan('downloadApp', Application, application => {
      const rootFolder = application.rootFolder
      return application.organization.hasCapability(OrganizationRoleCapability.downloadApps) || rootFolder.hasShareCapability(user, DocumentRoleCapability.downloadApp)
    });

    this.iCan('createApp', Application, application => {
      return application.organization.hasCapability(OrganizationRoleCapability.createApps)
    });

    this.iCan('uploadApp', Application, application => {
      const rootFolder = application.rootFolder
      return application.organization.hasCapability(OrganizationRoleCapability.uploadApps) || rootFolder.hasShareCapability(user, DocumentRoleCapability.uploadApp)
    });

    this.iCan('viewTagManager', Organization, organization => {
      return organization.hasCapability(OrganizationRoleCapability.editAllDocumentsAndFolders);
    });

    // groups & permissions

    this.iCan('manageAllGroups', Organization, organization => {
      return organization.hasCapability(OrganizationRoleCapability.manageAllGroups);
    });

    this.iCan('unassignGroup', Organization, (organization, group) => {
      return GroupDefinition.isOrgGroup(group) && this.can('manageAllGroups', organization)
    })
    
    this.iCan('viewGroupsAndPermissions', Application, application => {
      const orgRoles = [
        OrganizationRoleCapability.manageAppGroupsAndPermissions,
        OrganizationRoleCapability.transferAppGroupOwner
      ]
      const orgAbility = application.organization.hasCapability(orgRoles)

      const rootFolder = application.rootFolder
      const docAbility = rootFolder.hasShareCapability(user, appGroupDocCapabilities)

      // application groups are being pre-filtered by the server to only include groups that the current user has permission to access
      // if an user has permission to view a group, they are able to access the 'Groups and Permissions' page
      const groupAbility = application.groups.length > 0

      return orgAbility || docAbility || groupAbility
    })

    this.iCan('viewAppUsers', Application, application => {
      const orgRoles = [
        OrganizationRoleCapability.manageAppGroupsAndPermissions,
        OrganizationRoleCapability.transferAppGroupOwner,
        OrganizationRoleCapability.transferAppOwner,
      ]
      const orgAbility = application.organization.hasCapability(orgRoles)

      const rootFolder = application.rootFolder
      const docAbility = rootFolder.hasShareCapability(user, appGroupDocCapabilities)
      return orgAbility || docAbility
    })

    this.iCan('createAppGroups', Application, application => {
      const orgAbility = application.organization.hasCapability(OrganizationRoleCapability.manageAppGroupsAndPermissions)
      if (orgAbility) return true

      const rootFolder = application.rootFolder
      const docRoles = [DocumentRoleCapability.createAppGroups]
      const docAbility = rootFolder.hasShareCapability(user, docRoles)
      if (docAbility) return true

      return false
    })

    this.iCan('renameAppGroup', Application, (application, group) => {
      if (!GroupDefinition.isAppGroup(group)) return false

      const orgAbility = application.organization.hasCapability(OrganizationRoleCapability.manageAppGroupsAndPermissions)

      const rootFolder = application.rootFolder
      const docRoles = [DocumentRoleCapability.renameAppGroups]
      const docAbility = rootFolder.hasShareCapability(user, docRoles)

      return orgAbility || docAbility
    })

    this.iCan('deleteAppGroup', Application, (application, group) => {
      if (!GroupDefinition.isAppGroup(group)) return false;

      return user.id === ApplicationDefinition.owner(application, application.organization.app_owner_role_id) ||
             application.organization.hasCapability(OrganizationRoleCapability.manageAppGroupsAndPermissions) ||
             application.rootFolder.hasShareCapability(user, [DocumentRoleCapability.deleteAppGroups]) ||
             user.hasGroupCapability(GroupRoleCapability.deleteGroup, group.id);
    });

    this.iCan('inviteToAppGroup', Application, (application, group) => {
      if (!GroupDefinition.isAppGroup(group)) return false

      const orgAbility = application.organization.hasCapability(OrganizationRoleCapability.manageAppGroupsAndPermissions)

      const rootFolder = application.rootFolder
      const docRoles = [DocumentRoleCapability.inviteUsersToAppGroups]
      const docAbility = rootFolder.hasShareCapability(user, docRoles)

      return orgAbility || docAbility
    })

    this.iCan('transferAppGroupOwner', Application, (application, group) => {
      if (!GroupDefinition.isAppGroup(group)) return false

      const orgRoles = [
        OrganizationRoleCapability.manageAppGroupsAndPermissions,
        OrganizationRoleCapability.transferAppGroupOwner
      ]
      const orgAbility = application.organization.hasCapability(orgRoles)

      const rootFolder = application.rootFolder
      const docRoles = [DocumentRoleCapability.transferAppGroupOwner]
      const docAbility = rootFolder.hasShareCapability(user, docRoles)

      return orgAbility || docAbility
    })

    this.iCan('resendInvitation', Application, (application, group) => {
      if (!GroupDefinition.isAppGroup(group)) return false

      const orgRoles = [OrganizationRoleCapability.manageAppGroupsAndPermissions]
      const orgAbility = application.organization.hasCapability(orgRoles)

      const rootFolder = application.rootFolder
      const docRoles = [DocumentRoleCapability.inviteUsersToAppGroups]
      const docAbility = rootFolder.hasShareCapability(user, docRoles)

      const groupRoles = [GroupRoleCapability.manageMembers]
      const groupAbility = user.hasGroupCapability(groupRoles, group.id)

      return orgAbility || docAbility || groupAbility
    })

    this.iCan('exportAppPermissions', Application, application => {
      const orgRoles = [
        OrganizationRoleCapability.manageAppGroupsAndPermissions,
      ]
      const orgAbility = application.organization.hasCapability(orgRoles)

      const rootFolder = application.rootFolder
      const docRoles = [DocumentRoleCapability.exportAppPermissions]
      const docAbility = rootFolder.hasShareCapability(user, docRoles)

      return orgAbility || docAbility
    })

    this.iCan('exportAppUserPermissions', Application, (application, user: IMemberUser) => {
      if (!user) return false
      if (!user.hasOwnProperty('status')) return false
      if (userIsDeactivated(user)) return false

      return this.can('exportAppPermissions', application)
    })

    this.iCan('transferAppOwnership', Application, (application, {appOwner, proposedAppOwner}: {appOwner: IMemberUser, proposedAppOwner: IMemberUser}) => {
      // Can't transfer if can't verify the current owner
      if (!appOwner) return false
      // Can't transfer if already the owner
      if (appOwner.id === proposedAppOwner.id) return false
      // Can't transfer if the proposed owner is a pending member
      if (proposedAppOwner.status === 'Pending') return false

      const orgRoles = [
        OrganizationRoleCapability.transferAppOwner
      ]
      const orgAbility = application.organization.hasCapability(orgRoles)

      const rootFolder = application.rootFolder
      const docRoles = [DocumentRoleCapability.transferAppOwner]
      const docAbility = rootFolder.hasShareCapability(user, docRoles)

      return orgAbility || docAbility
    })

    this.iCan('removeAppGroupMembers', Application, (application, group) => {
      if (!GroupDefinition.isAppGroup(group)) return false;

      return this.can('removeAppUser', application) ||
             user.hasGroupCapability(GroupRoleCapability.manageMembers, group.id);
    });

    this.iCan('removeAppUser', Application, (application) => {
      return application.organization.hasCapability(OrganizationRoleCapability.manageAppGroupsAndPermissions) ||
             application.rootFolder.hasShareCapability(user, DocumentRoleCapability.removeUsersFromAppGroups);
    });

    this.iCan('viewAppUserDocPermissions', Application, (application, user: IMemberUser) => {
      return !userIsDeactivated(user) && this.can('viewAppUsers', application)
    })
  }
}

const userIsDeactivated = (user: IMemberUser): boolean => {
  return user.status === 'Deactivated'
}
